<template>
  <div ref="dropdown">
    <button
      @click="toggle"
      :id="id"
      class="h-100 w-100"
      :class="buttonClassesComp"
      ref="button"
      :aria-controls="id + 'menu'"
      :active="dropdownState.active"
      aria-haspopup="menu">
      <slot name="button-content" :active="dropdownState.active">{{
        text
      }}</slot>
    </button>

    <transition name="dropdown-content">
      <ul
        class="dropdown-menu show"
        ref="dropdownContent"
        :style="floatingStyles"
        :class="menuClasses"
        v-show="dropdownState.active"
        :aria-labelledby="id"
        :id="id + 'menu'"
        :role="role">
        <slot />
      </ul>
    </transition>
  </div>
</template>

<script setup lang="ts">
import {
  autoUpdate,
  flip,
  offset as floatingOffset,
  shift,
  useFloating,
  type Middleware,
  type OffsetOptions,
  type Placement,
  type Strategy,
} from "@floating-ui/vue";
import { onClickOutside } from "@vueuse/core";
import { computed, provide, ref } from "vue";

export interface DropdownState {
  active: boolean;
}

interface Props {
  id: string;
  buttonSize?: "sm" | "md" | "lg";
  buttonVariant?: string;
  text?: string;
  noButtonStyle?: boolean;
  buttonClasses?: string;
  menuClasses?: string;
  noCaret?: boolean;
  placement?: Placement;
  strategy?: Strategy;
  offset?: number | OffsetOptions;
  noShift?: boolean;
  noFlip?: boolean;
  floatingMiddleware?: Middleware[];
  role?: string;
}

const props = withDefaults(defineProps<Props>(), {
  text: "toggle",
  buttonSize: "sm",
  buttonVariant: "secondary",
  placement: "bottom-start",
  offset: 0,
  role: "menu",
});

/** Events */
const emits = defineEmits<{
  /**
   *  Emitted when the dropdown is hidden.
   */
  (e: "hide", event: Event): void;
  /**
   *  Emitted when the dropdown is shown.
   */
  (e: "show", event: Event): void;
  /**
   *  Emitted when the toggle is clicked.
   */
  (e: "toggle", event: Event): void;
}>();

const button = ref(null);
const dropdownContent = ref(null);
const dropdown = ref<HTMLElement | null>();

// Sets up object to track dropdown state
const dropdownState = ref<DropdownState>({
  active: false,
});

provide("rds-dropdown-state", dropdownState);

// dropdown anchoring and floating ui setup
const floatingMiddleware = computed<Middleware[]>(() => {
  if (props.floatingMiddleware !== undefined) {
    return props.floatingMiddleware;
  }

  const arr: Middleware[] = [floatingOffset(props.offset)];

  props.noShift ? null : arr.push(shift());

  props.noFlip ? null : arr.push(flip());

  return arr;
});

const { floatingStyles } = useFloating(button, dropdownContent, {
  placement: props.placement,
  strategy: "absolute",
  whileElementsMounted: autoUpdate,
  middleware: floatingMiddleware,
});

const buttonClassesComp = computed(() => {
  let classes = " ";

  props.noButtonStyle
    ? (classes += ` btn-unset `)
    : (classes += ` btn btn-${props.buttonVariant} btn-${props.buttonSize}`);

  props.buttonClasses ? (classes += props.buttonClasses) : null;

  props.noCaret
    ? (classes += " dropdown-toggle-no-caret ")
    : (classes += " dropdown-toggle ");

  return classes;
});

onClickOutside(dropdown, (event) => {
  dropdownState.value.active ? close(event) : null;
});

const toggle = (e: Event) => {
  dropdownState.value.active ? close(e) : open(e);
  emits("toggle", e);
};

const close = (e: Event) => {
  dropdownState.value.active = false;
  emits("hide", e);
};

const open = (e: Event) => {
  dropdownState.value.active = true;
  emits("show", e);
};
</script>

<style lang="scss" scoped>
.btn-unset {
  all: unset;
  cursor: pointer;
  &:focus {
    outline: 2px solid var(--rds-dark-3, #191919);
  }
}
</style>
